import { Autocomplete, Divider, Paper, TextField, Drawer, Box } from "@mui/material"
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { usePageLoader } from "../contexts/page_content_context";
import { ClienteDropDown } from "../domain/models/cliente_dropdown";
import { usePageStartBootstrap } from "../hooks/use_page_start_bootstrap";
import { ClienteSimplificadoForm, ClienteSimplificadoSchema, defaultClienteSimplificadoForm } from "../presentation/schemas/ClienteSimplificado";
import { zodResolver } from "@hookform/resolvers/zod";

import DynamicIcon from "./dynamic_icon";
import DefaultFormInput from "./default_form_input";
import PrimaryButton from "./primary_button";
import { FormCNPJMaskedInput } from "./masked_inputs/react_forms/form_cnpj_masked_input";

/**
 * DropDownCliente
 */

type Props<F extends {}> = {
  name: keyof F,
  label?: string,
  newButtonLabel?: string,
  required?: boolean,
  readOnly?: boolean,
  placeholder?: string,
  reactHookForm: UseFormReturn<F>,

};

export const DropDownClienteEmpresa = <F extends {},>({
  name,
  label,
  newButtonLabel,
  required,
  readOnly,
  placeholder,
  reactHookForm,

}: Props<F>) => {


  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { pushErrorNotification, pushInfoNotification } = usePageLoader();


  const [clienteDropDown, setClienteDropDown] = useState<ClienteDropDown[]>([]);

  const {
    commons: {
      getClientesDropDownUseCase,
      createClienteSimplificadoUseCase,
    }

  } = useDependencyInjection();


  const hookForm = useForm<ClienteSimplificadoForm>({
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    defaultValues: defaultClienteSimplificadoForm,
    resolver: zodResolver(ClienteSimplificadoSchema),
  });

  //Load Clientes DropDown

  const loadClientesDropDown = useCallback(async () => {
    try {
      const result = await getClientesDropDownUseCase.getClientesDropDown();
      setClienteDropDown(result);
    } catch (e: unknown) {
      throw new Error('Falha ao buscar clientes!');
    }
  }, [
    getClientesDropDownUseCase,
  ]);

  //Create Centro de Custo

  const hanldleSalvarCliente = useCallback(async (form: ClienteSimplificadoForm) => {
    try {
      setSaving(true);
      const idCliente = await createClienteSimplificadoUseCase.createClienteSimplificado(
        {
          nomeCliente: form.nomeCliente,
          cpf: null,
          dataNascimento: new Date,
          observacao: form.observacao,
          razaoSocial: form.razaoSocial,
          cnpj: form.cnpj,
        }
      );

      reactHookForm.setValue(name as any, idCliente as any);
      pushInfoNotification("SUCESSO", "Cliente criado com sucesso!!", { isAutoFinish: true })

    } catch (e: unknown) {
      pushErrorNotification("Erro", ["Falha ao salvar Cliente"], { isAutoFinish: true })
    } finally {
      setSaving(false);
      loadClientesDropDown();
      setOpenModal(false);
    }

  }, [createClienteSimplificadoUseCase, loadClientesDropDown, name, pushErrorNotification, pushInfoNotification, reactHookForm]);

  usePageStartBootstrap(async () => {
    loadClientesDropDown()
  })


  // Add Centro Custo Button
  const CustomPaper = useMemo(
    () =>
      ({ children, ...props }: any) => (
        <Paper {...props}>
          <div>
            {children}
            <Divider />
            <div>
              <div
                className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold flex items-center gap-2 text-sm text-primary-green hover:bg-hover-light-gray"
                onMouseDown={() => setOpenModal(true)}
                role="button"
                tabIndex={0}>
                <DynamicIcon
                  nameIcon="Add"
                  size={20}
                  className="text-primary-green"
                />
                <span>{newButtonLabel ?? "Novo"}</span>
              </div>
            </div>
          </div>
        </Paper>
      ),
    [newButtonLabel]
  );



  return (
    <>

      {/*Centro Custo DropDown */}

      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (formState.errors[name as keyof typeof formState.errors]) {
            const fieldError = formState.errors[name] as any;
            if (fieldError?.message && typeof fieldError.message === 'string') {
              helperText = fieldError.message;
            }
          }


          return (
            <Autocomplete
              fullWidth={true}
              size="small"
              disablePortal
              options={clienteDropDown}
              getOptionKey={opt => opt.id}
              getOptionLabel={(opt) => opt.title}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              slots={{
                paper: CustomPaper,
              }}
              sx={{ minHeight: 45 }}
              readOnly={readOnly}
              disabled={formState.disabled}
              value={clienteDropDown.find(x => x.id === field.value) ?? null}
              onChange={(_, newValue) => field.onChange(newValue ? newValue.id : undefined)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={label}
                  error={helperText !== null}
                  helperText={helperText}
                  placeholder={placeholder}
                  required={required}
                />
              )}
            />
          );
        }}
      />

      {/* Add Centro Custo Modal */}

      <Drawer
        anchor="right"
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{ zIndex: 1301 }}
        PaperProps={{ style: { width: 500, padding: 20, display: "flex", flexDirection: "column", height: "100%" } }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <h2 className="text-lg font-semibold mb-4">Adicionar Cliente</h2>


          <div className="flex flex-col items-start w-[25rem] gap-4">


            <DefaultFormInput
              name='nomeCliente'
              label='Nome Fantasia'
              required={true}
              reactHookForm={hookForm} />

            <DefaultFormInput
              name='razaoSocial'
              label='Razão Social'
              required={true}
              reactHookForm={hookForm} />

            <FormCNPJMaskedInput
              name='cnpj'
              label='CNPJ'
              required={false}
              reactHookForm={hookForm}

            />

            <DefaultFormInput
              name='observacao'
              label='Observações'
              multiline={true}
              minRows={6}
              required={false}
              reactHookForm={hookForm} />

          </div>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "white",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >

          <Box
            className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
            onClick={() => setOpenModal(false)}
          >
            <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
          </Box>

          <Box width="120px">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">
                Salvando...
              </PrimaryButton>
            ) : (
              <PrimaryButton
                buttonColor="primary-green"
                onClick={hookForm.handleSubmit(hanldleSalvarCliente)}
              >
                Salvar
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Drawer>

    </>
  );
};
